import { DataAccess, ResponseMessage } from "../dataaccess/data.access";
import { EventService } from "./event.service";
import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { BehaviorSubject } from "rxjs";
import { IntegrationService, IntegrationTypes } from "../modules/integrations/integration.service";
import { LoginService } from "./login.service";
import { normalizeSocChannelType, SocChannelType } from "./soc.chat.channel.util";

export type { SocChannelType };

export const SocChatExternalType = {
    Event: "Event",
    Workflow: "Workflow",
} as const;

export type SocChatExternalTypeValue = (typeof SocChatExternalType)[keyof typeof SocChatExternalType];

export class StandoutClassroomService {
    private static isRecurringCheckRunning = false;
    private static unreadChatsSubject = new BehaviorSubject<UnreadChat[]>([]);
    private static pollingIntervalMs = 2 * 60 * 1000; // 2 minutes

    public static getChatLink() {
        return StandoutClassroomService.getRedirectUrl(`/init/chat`);
    }

    public static async getEventChatLink(eventId: number) {
        let eventLocator = await EventService.getEventLocator(eventId);
        eventLocator = eventLocator ? ` (${eventLocator})` : "";
        const channelName = (await EventService.getEventNameOrTitle(eventId)) + eventLocator;

        const query = new URLSearchParams({
            title: channelName,
            externalType: SocChatExternalType.Event,
        });

        return StandoutClassroomService.getRedirectUrl(`/init/chat/private/channel/${eventId}?${query.toString()}`);
    }

    public static async getUnreadChatInfo() {
        const jwtResponse = await StandoutClassroomService.getAuthToken();
        if (!jwtResponse?.token) {
            return [];
        }
        let [unread, _error] = await S25Util.Maybe(
            DataAccess.get<UnreadChats>(StandoutClassroomService.getWsUrl("/data/qk/user/channels/unread.json"), {
                headers: {
                    Authorization: "Bearer " + jwtResponse.token,
                },
            }),
        );
        unread ??= {};
        unread.qkChannelTO ??= [];
        return unread.qkChannelTO;
    }

    /**
     * Returns a promise that either kicks off the first request + recursion
     * OR sees that it's already going and returns the cached response
     */
    public static async getUnreadChatsWithRecurringCheck(): Promise<UnreadChat[]> {
        // Check if SoC integration exists before starting polling
        let integration = await IntegrationService.getIntegration(IntegrationTypes.soc);
        if (!integration) {
            // if Row doesn't exist yet create it (mirrors what RedirectController does on first chat-link click)
            integration = await IntegrationService.createIntegration({
                integrationType: IntegrationTypes.soc,
                enabled: true,
                apiKey: "",
            });
        }
        if (!integration) {
            // No integration exists, return empty array and don't start polling
            return [];
        }

        if (!StandoutClassroomService.isRecurringCheckRunning) {
            // Start the first request and set up recursion
            StandoutClassroomService.isRecurringCheckRunning = true;
            await StandoutClassroomService.startUnreadChatsCheck();
        }
        return StandoutClassroomService.unreadChatsSubject.value;
    }

    /**
     * Returns an observable that emits whenever unread chats are updated
     */
    public static getUnreadChatsObservable() {
        return StandoutClassroomService.unreadChatsSubject.asObservable();
    }

    /**
     * Forces an immediate refresh of unread chats, bypassing the polling interval.
     * Use this after creating a new chat channel or when the user returns to the page.
     */
    public static async forceRefreshUnreadChats(): Promise<void> {
        if (!StandoutClassroomService.isRecurringCheckRunning) {
            return;
        }
        try {
            if (!(await LoginService.getLoggedIn())) {
                return;
            }
            const unreadChats = await StandoutClassroomService.getUnreadChatInfo();
            StandoutClassroomService.unreadChatsSubject.next(unreadChats);
        } catch (error) {
            console.error("Error force refreshing unread chats:", error);
        }
    }

    private static async startUnreadChatsCheck(): Promise<void> {
        try {
            // check if user is logged in
            if (!(await LoginService.getLoggedIn())) {
                console.log("User is not logged in, skipping unread chats check");
                StandoutClassroomService.isRecurringCheckRunning = false;
                return;
            }

            const unreadChats = await StandoutClassroomService.getUnreadChatInfo();

            // Emit the updated unread chats to all subscribers
            StandoutClassroomService.unreadChatsSubject.next(unreadChats);

            // Set up the next check
            setTimeout(() => {
                StandoutClassroomService.startUnreadChatsCheck();
            }, StandoutClassroomService.pollingIntervalMs);
        } catch (error) {
            console.error("Error fetching unread chats:", error);
            StandoutClassroomService.isRecurringCheckRunning = false; // Reset on error
        }
    }

    private static getRedirectUrl(subPath: string) {
        let url = DataAccess.getUrl(`/redirect/app?app=soc&redirect=${encodeURIComponent(subPath)}`);
        const hostname = window.location.hostname;
        if (hostname === "localhost" || hostname.includes("unival")) {
            url += "&fullToken=true";
        }
        return url;
    }

    public static getBaseWsUrl() {
        if (S25Const.isWebDevInstance) {
            return `http://25live-web-dev.unival.com/${S25Const.instanceId}/standout-classroom`;
        }
        return `https://series25.com/${S25Const.instanceId}/standout-classroom`;
    }

    private static getWsUrl(path: string = ""): string {
        if (!path.startsWith("/")) {
            path = "/" + path;
        }
        return this.getBaseWsUrl() + path;
    }

    /** Origin + path prefix for SOC email deep links (no hash/query). Same pattern as getEventDetailsUrl(). */
    private static get25LiveCreateReferer(): string {
        return S25Util.getWindowOrigin() + S25Util.getWindowPath();
    }

    /**
     * Creates or retrieves an existing SoC DM channel for the given users.
     * Calls SoC's postByContacts endpoint directly from the frontend using the current user's JWT.
     * Returns the channel externalId (UUID).
     */
    public static async createDmChannelFromUsers(users: SocContactUser[]): Promise<SocPublicChannelResult> {
        const jwtResponse = await StandoutClassroomService.getAuthToken();
        if (!jwtResponse?.token) throw new Error("Unable to authenticate with chat service");

        const url = StandoutClassroomService.getWsUrl("/data/qk/channel/direct/post/contacts.json");
        const payload = JSON.stringify({
            IncarnateBatchRequestTO: {
                baseUrl: StandoutClassroomService.getBaseWsUrl() + "/",
                createReferer: StandoutClassroomService.get25LiveCreateReferer(),
                users,
            },
        });

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + jwtResponse.token,
            },
            body: payload,
        });

        if (!response.ok) throw new Error(`SoC DM channel creation failed: HTTP ${response.status}`);

        const data = await response.json();
        const qkChannel = data?.qkChannelTO;
        const externalId = qkChannel?.externalId ?? String(qkChannel?.channelId ?? "");
        if (!externalId) throw new Error("externalId missing from SoC response");
        return { ...StandoutClassroomService.parseQkChannelResult(data, externalId), channelType: "direct" };
    }

    public static async createPrivateChannelFromUsers(
        users: SocContactUser[],
        externalId: string,
        externalType: string,
        title?: string,
    ): Promise<SocPublicChannelResult> {
        const jwtResponse = await StandoutClassroomService.getAuthToken();
        if (!jwtResponse?.token) throw new Error("Unable to authenticate with chat service");

        const url = StandoutClassroomService.getWsUrl("/data/qk/channel/private/post/contacts.json");
        const payload = JSON.stringify({
            QkChannelPrivateRequestTO: {
                baseUrl: StandoutClassroomService.getBaseWsUrl() + "/",
                createReferer: StandoutClassroomService.get25LiveCreateReferer(),
                users,
                externalId,
                externalType,
                title,
            },
        });

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + jwtResponse.token,
            },
            body: payload,
        });

        if (!response.ok) throw new Error(`SoC private channel creation failed: HTTP ${response.status}`);

        const data = await response.json();
        return StandoutClassroomService.parseQkChannelResult(data, externalId);
    }

    public static async createPublicChannelFromUsers(
        users: SocContactUser[],
        externalId: string,
        externalType: string,
        title?: string,
    ): Promise<SocPublicChannelResult> {
        const jwtResponse = await StandoutClassroomService.getAuthToken();
        if (!jwtResponse?.token) throw new Error("Unable to authenticate with chat service");

        const url = StandoutClassroomService.getWsUrl("/data/qk/channel/public/post/contacts.json");
        const payload = JSON.stringify({
            QkChannelPublicRequestTO: {
                baseUrl: StandoutClassroomService.getBaseWsUrl() + "/",
                createReferer: StandoutClassroomService.get25LiveCreateReferer(),
                users,
                externalId,
                externalType,
                title,
            },
        });

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + jwtResponse.token,
            },
            body: payload,
        });

        if (!response.ok) throw new Error(`SoC public channel creation failed: HTTP ${response.status}`);

        const data = await response.json();
        return StandoutClassroomService.parseQkChannelResult(data, externalId);
    }

    private static parseQkChannelResult(
        data: { qkChannelTO?: SocQkChannelResponse; skippedUsers?: SocSkippedUser[] },
        fallbackExternalId: string,
    ): SocPublicChannelResult {
        const qkChannel = data?.qkChannelTO;
        return {
            externalId: qkChannel?.externalId ?? fallbackExternalId,
            channelType: normalizeSocChannelType(qkChannel?.type),
            socChannelId: qkChannel?.channelId,
            skippedUsers: data?.skippedUsers ?? [],
        };
    }

    public static async getAuthToken(): Promise<SocJwtResponse | null> {
        const offsetSec = 60; // 1 minute offset to ensure the token stays valid during the request
        const storedData = sessionStorage.getItem("socJwt");
        if (storedData) {
            try {
                const jwtData: SocJwtResponse = JSON.parse(storedData);
                if (jwtData.token && jwtData.expirationTimestamp) {
                    const currentTimeSec = Date.now() / 1000;
                    if (jwtData.expirationTimestamp > currentTimeSec + offsetSec) {
                        return jwtData;
                    }
                }
            } catch (e) {
                // Invalid format, will refresh below
            }
        }
        // if the token is not valid or expired (or too close to expired for comfort), we need to get a new one
        return await StandoutClassroomService.getFreshSocJwt();
    }

    private static async getFreshSocJwt(): Promise<SocJwtResponse | null> {
        let fullToken = "";
        const hostname = window.location.hostname;
        if (hostname === "localhost" || hostname.includes("unival")) {
            fullToken += "?fullToken=true";
        }
        const resp = await DataAccess.get<SocJwtResponse>(`/soc/jwt.json${fullToken}`);
        if (resp?.token) {
            // Store both token and expiration as JSON
            const jwtData: SocJwtResponse = {
                token: resp.token,
                expirationTimestamp: resp.expirationTimestamp,
            };
            sessionStorage.setItem("socJwt", JSON.stringify(jwtData));
            return jwtData;
        }
        return null;
    }
}

// data shape pulled from: https://collegenet.atlassian.net/browse/SOC-612
export interface UnreadChats {
    qkChannelTO?: UnreadChat[];
}

export interface UnreadChat {
    channelId?: number;
    unreadCount?: number;
    creatorId?: number;
    title?: string;
    type?: "public" | "private";
    uuid?: string;
    externalId?: string;
    createDt?: string;
    lastModDt?: string;
}

export interface SocJwtResponse {
    token?: string;
    expirationTimestamp?: number;
}

export interface SocContactUser {
    userType: string;
    contId: number;
    username: string;
    firstname: string;
    lastname: string;
    email: string;
}

export interface SocPublicChannelResult {
    externalId: string;
    channelType: SocChannelType;
    socChannelId?: number;
    skippedUsers: SocSkippedUser[];
}

interface SocQkChannelResponse {
    channelId?: number;
    externalId?: string;
    type?: string;
}

export interface SocSkippedUser {
    username?: string;
    contId?: number | string;
    reason?: string;
}
